import Component from "../../lib/component";
import ComponentEvent from "../../lib/component_event";
import {fireElementEvent} from "../../helpers/event";

export const CollapsableFieldsetConfig = {
    componentTypeKey: 'CollapsableFieldset',
    baseHtmlClass: 'collapsable',
    selector: '.collapsable',

    errorHtmlClass: 'error',

    collapsedHtmlClassModifier: 'collapsed',
    openHtmlClassModifier: 'open',
    closedHtmlClassModifier: 'closed',
    errorHtmlClassModifier: 'hasError',

    openedEventName: 'Rankers.CollapsableFieldset:Opened',
    closedEventName: 'Rankers.CollapsableFieldset:Closed',
    toggledEventName: 'Rankers.CollapsableFieldset:Toggled',
};

export class CollapsableFieldset extends Component {

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: CollapsableFieldsetConfig
        });

        this.formElement = this.element.closest('form');
        this.legendElement = this.element.querySelector('legend');
        if (!this.legendElement) return;
        this.inputItemElements = this.element.querySelectorAll('ol > li');

        this.isClosed = true;
        if (this.inputItemElementsHasError()) this.isClosed = false;
        if (this.hasHtmlClassState(CollapsableFieldsetConfig.openHtmlClassModifier)) this.isClosed = false;
        if (this.hasHtmlClassState(CollapsableFieldsetConfig.closedHtmlClassModifier)) this.isClosed = true;
        // Last, so it beats every default above: someone followed a link TO a field
        // in here, which is a stronger signal than the markup's preferred state.
        if (this.containsHashTarget()) this.isClosed = false;

        this.legendElement.addEventListener('click', this.handleLegendClick.bind(this));

        // A hash can also arrive without a page load - pasting a link while already
        // on the page, or an in-page anchor. Window listener, so it needs unwiring.
        const onHashChange = this.handleHashChange.bind(this);
        window.addEventListener('hashchange', onHashChange);
        this.onDestroy(() => window.removeEventListener('hashchange', onHashChange));

        this.render();
        this.scrollToHashTarget();
    }

    handleHashChange() {
        if (!this.containsHashTarget()) return;

        this.isClosed = false;
        this.render();
        this.scrollToHashTarget();
    }

    // The element the current URL fragment points at, when it lives in this fieldset.
    //
    // getElementById rather than querySelector: an id is not required to be a valid
    // CSS selector, and these are generated from attribute names, so a selector built
    // by hand can throw on ids a browser accepts perfectly well.
    hashTargetElement() {
        const hash = window.location.hash;
        if (!hash || hash.length < 2) return null;

        let id;
        try {
            id = decodeURIComponent(hash.slice(1));
        } catch (error) {
            return null; // A malformed escape sequence is not a target.
        }

        const target = document.getElementById(id);
        return (target && this.element.contains(target)) ? target : null;
    }

    containsHashTarget() {
        return !!this.hashTargetElement();
    }

    // The browser tried to jump here on load and could not: the field was inside
    // `ol { display: none }` (see _collapsable.scss), and a hidden element cannot be
    // scrolled to. Opening the fieldset does not give the native jump a second go,
    // so the scroll has to be made explicitly once the field is visible.
    scrollToHashTarget() {
        if (this.isClosed) return;

        const target = this.hashTargetElement();
        if (!target || !target.scrollIntoView) return;

        target.scrollIntoView({ block: 'center' });
    }

    handleLegendClick() {
        this.fireCollapsableFieldsetEvent(this.isClosed ? CollapsableFieldsetConfig.openedEventName : CollapsableFieldsetConfig.closedEventName);
        this.fireCollapsableFieldsetEvent(CollapsableFieldsetConfig.toggledEventName);
        this.isClosed = !this.isClosed;
        this.render();
    }

    fireCollapsableFieldsetEvent(eventName) {
        const componentEvent = new ComponentEvent(eventName, {target: this});
        this.fireEvent(componentEvent);
        fireElementEvent(this.element, eventName); // Also fire the event on the element.
    }

    render() {
        this.swapHtmlClassStates(this.isClosed, CollapsableFieldsetConfig.closedHtmlClassModifier, CollapsableFieldsetConfig.openHtmlClassModifier);
        this.conditionallySetHtmlClassState(this.inputItemElementsHasError(), CollapsableFieldsetConfig.errorHtmlClassModifier);
    }

    inputItemElementsHasError() {
        // The braces here used to wrap the expression without returning it, so this
        // always answered undefined and a fieldset holding a validation error stayed
        // shut - the one moment it most needs to be open.
        return [...this.inputItemElements].some((element) => element.classList.contains(CollapsableFieldsetConfig.errorHtmlClass));
    }
}

